/**
 * Request and Response Interceptors
 */
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import type { UseBoundStore, StoreApi } from 'zustand';
import type { AppStore } from '../state/types';
import { showError } from '../helpers/toast';
import { remove, save } from '../helpers/storage';
import { STORAGE_KEYS } from '../constants/storage';

let store: UseBoundStore<StoreApi<AppStore>> | null = null;

export const setStore = (appStore: UseBoundStore<StoreApi<AppStore>>) => {
  store = appStore;
};

/**
 * Request Interceptor
 * Adds authentication token and headers to requests
 */
export const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  if (!store) {
    return config;
  }

  let token = '' as any;
  if (store.getState().token) {
    token = store.getState().token;
  } else {
    token = localStorage.getItem(STORAGE_KEYS.token);
    if (token) {
      save(STORAGE_KEYS.token, token);
    }
  }

  // Add auth token if available
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add default headers
  if (!config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  // Add app header (required for all requests)
  if (!config.headers['app']) {
    config.headers['app'] = 'anstmasr2588';
  }

  // Add x-portal header (required for admin endpoints)
  if (!config.headers['x-portal']) {
    config.headers['x-portal'] = 'admin';
  }

  // Mark as guest request if no token
  if (!token && !config.headers['X-Guest-Request']) {
    config.headers['X-Guest-Request'] = 'true';
  }

  return config;
};

/**
 * Response Error Interceptor
 * Handles global error responses
 */
export const responseErrorInterceptor = async (error: AxiosError<any>) => {
  if (!error.response) {
    // Network error
    showError('Network error. Please check your connection.');
    return Promise.reject(error);
  }

  const { status, data } = error.response;

  switch (status) {
    case 401:
      // Unauthorized - clear token and redirect to login
      if (store) {
        store.getState().reset();
        await remove(STORAGE_KEYS.token);
      }
      // Redirect to login page
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      showError(data?.message || 'Session expired. Please login again.');
      break;

    case 403:
      // Forbidden
      showError(data?.message || 'You do not have permission to perform this action.');
      break;

    case 404:
      showError(data?.message || 'Resource not found.');
      break;

    case 422:
      // Validation error
      const validationErrors = data?.errors || {};
      const errorValues = Object.values(validationErrors);
      const firstError = (errorValues[0] as string[])?.[0] || data?.message;
      showError(firstError || 'Validation error.');
      break;

    case 500:
      showError(data?.message || 'Internal server error. Please try again later.');
      break;

    case 502:
      // Bad Gateway
      showError('Server is temporarily unavailable. Please try again later.');
      break;

    default:
      showError(data?.message || `An error occurred (${status}).`);
  }

  return Promise.reject(error);
};

/**
 * Setup interceptors on axios instance
 */
export const setupInterceptors = (
  axiosInstance: AxiosInstance,
  appStore: UseBoundStore<StoreApi<AppStore>>
) => {
  setStore(appStore);

  // Request interceptor
  axiosInstance.interceptors.request.use(
    requestInterceptor,
    (error) => Promise.reject(error)
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    responseErrorInterceptor
  );
};

